import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Input, Typography, Space, Spin, Empty, message } from 'antd';
import { CheckCircleOutlined, TeamOutlined, InfoCircleFilled } from '@ant-design/icons';
import { ensureListing } from '../../util/data';
import { getListingsByIds } from '../../util/api';
import css from './ConsultantSelectionModal.module.css';

const { Text, Title } = Typography;

const SEARCH_DEBOUNCE_MS = 300;

const loadCandidateListings = ids => getListingsByIds({ ids, listingType: 'candidate' });

/**
 * ConsultantSelectionModal
 *
 * Lists the agency's accepted consultants a page at a time, as the server answers them, and reads
 * profile titles only for the consultants shown.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.listing - The job listing
 * @param {Function} props.onApplyWithConsultant - Called with (jobId, consultantId, pickedRow)
 * @param {Array} props.localAppliedConsultants - Consultant listing ids applied with in this visit
 * @param {Function} props.onSearch - (text) => Promise<{ ok, items }> of accepted consultants
 * @param {Function} props.onLoadListings - (listingIds) => Promise of the profile listings
 */
const ConsultantSelectionModal = props => {
  const {
    isOpen,
    onClose,
    listing,
    onApplyWithConsultant,
    localAppliedConsultants = [],
    onSearch,
    onLoadListings = loadCandidateListings,
  } = props;

  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [titles, setTitles] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  const currentListing = ensureListing(listing);
  const appliers = currentListing?.attributes?.publicData?.appliers;

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
      setSelectedConsultant(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !onSearch) return undefined;
    let current = true;
    const run = async () => {
      setLoading(true);
      const answer = await onSearch(searchText);
      if (!current) return;
      const found = answer?.ok ? answer.items || [] : [];
      setLoadFailed(!answer?.ok);
      setRows(found);
      setLoading(false);

      const listingIds = found.map(r => r.listingId).filter(Boolean);
      if (listingIds.length === 0) return;
      try {
        const response = await onLoadListings(listingIds);
        if (!current) return;
        const listings = response?.data?.data || [];
        setTitles(
          listings.reduce((acc, l) => ({ ...acc, [l.id?.uuid]: l.attributes?.title }), {})
        );
      } catch (e) {
        // The consultant's name stands in for a title that cannot be read.
      }
    };
    const timer = setTimeout(run, searchText ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [isOpen, searchText]);

  const isAppliedRow = row =>
    (Array.isArray(appliers) && appliers.includes(row.listingId)) ||
    localAppliedConsultants.includes(row.listingId);

  const handleApply = async () => {
    if (!selectedConsultant) {
      message.error("Please select a consultant to apply with");
      return;
    }

    setApplying(true);
    try {
      await onApplyWithConsultant(currentListing.id.uuid, selectedConsultant.id, selectedConsultant);
      setSelectedConsultant(null); // Reset selection
      onClose();
    } catch (error) {
      message.error("Failed to send application. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined />
          <span>Select Consultant for Application</span>
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      onOk={handleApply}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleApply}
          disabled={!selectedConsultant}
          loading={applying}
        >
          Apply with Selected Consultant
        </Button>,
      ]}
    >
      <div className={css.modalContent}>
        <div style={{marginBottom: "8px"}}>
          <InfoCircleFilled color='yellow' />
          <Text strong> Consultants who have already applied cannot be selected.</Text>
        </div>
        <Input
          allowClear
          placeholder="Search your consultants by name"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ marginBottom: '12px' }}
        />
        {loading ? (
          <div className={css.loadingContainer}>
            <Spin size="large" />
            <Text>Finding matching consultants...</Text>
          </div>
        ) : loadFailed ? (
          <Empty
            description="We couldn't load your consultants. Please try again."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : rows.length === 0 ? (
          <Empty
            description="No matching consultants found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className={css.consultantsList}>
            {rows.map(row => {
              const isSelected = selectedConsultant?.id === row.id;
              const isApplied = isAppliedRow(row);

              return (
                <Card
                  key={row.id}
                  className={`${css.consultantCard} ${isSelected ? css.selected : ''} ${isApplied ? css.applied : ''}`}
                  hoverable={!isApplied}
                  onClick={isApplied ? undefined : () => setSelectedConsultant(row)}
                  size="small"
                >
                  <div className={css.consultantHeader}>
                    <Space>
                      <div className={css.consultantInfo}>
                        <Title level={5} style={{ margin: 0 }}>
                          {titles[row.listingId] || row.label}
                        </Title>
                      </div>
                    </Space>
                  </div>

                  {isApplied ? (
                    <div className={css.appliedIndicator}>
                      Applied
                    </div>
                  ) : isSelected && (
                    <div className={css.selectedIndicator}>
                      <CheckCircleOutlined /> Selected
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConsultantSelectionModal;